import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clear, Email, RadioButtonChecked } from '@mui/icons-material';
import {
  CountersContainer,
  UsedLicensesContainer,
  LicenseNumber,
  AvailableLicenses,
  ParagraphContainer,
} from './styles';
import { Box, Button, Divider, Skeleton, Tooltip } from '@mui/material';

interface UserCountersProps {
  activeUsers: number;
  invitedUsers: number;
  inactiveUsers: number;
  availableLicenses: number;
  showSkeleton?: boolean;
  onAddUser?: () => void;
  onToggleActiveFilter?: () => void;
  onToggleInvitedFilter?: () => void;
  onToggleInactiveFilter?: () => void;
  isActiveFilterApplied?: boolean;
  isInvitedFilterApplied?: boolean;
  isInactiveFilterApplied?: boolean;
}

const SkeletonCounterValue = ({ value }: { value: string }) => (
  <Box sx={{ position: 'relative', display: 'inline-block' }}>
    <LicenseNumber style={{ visibility: 'hidden' }}>{value}</LicenseNumber>
    <Skeleton
      variant="text"
      animation="wave"
      sx={{
        position: 'absolute',
        inset: 0,
        fontSize: '32px',
        lineHeight: 1,
        width: '100%',
        transform: 'none',
        display: 'block',
      }}
    />
  </Box>
);

const SkeletonLabel = ({ children }: { children: string }) => (
  <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
    <ParagraphContainer style={{ visibility: 'hidden' }}>{children}</ParagraphContainer>
    <Skeleton
      variant="text"
      animation="wave"
      sx={{
        position: 'absolute',
        inset: 0,
        fontSize: '14px',
        lineHeight: 1.2,
        width: '100%',
        transform: 'none',
        display: 'block',
      }}
    />
  </Box>
);

const SkeletonActionButton = ({
  icon,
  text,
  sx,
}: {
  icon?: React.ReactNode;
  text: string;
  sx: Record<string, unknown>;
}) => (
  <Button
    type="button"
    disabled
    disableRipple
    sx={{
      position: 'relative',
      width: '100%',
      textTransform: 'none',
      overflow: 'hidden',
      '&.Mui-disabled': {
        opacity: 1,
        WebkitTextFillColor: 'unset',
        color: 'inherit',
      },
      ...sx,
    }}
  >
    <Box
      sx={{
        visibility: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '.4em',
      }}
    >
      {icon}
      {text}
    </Box>

    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        pointerEvents: 'none',
      }}
    >
      <Skeleton
        variant="rounded"
        animation="wave"
        sx={{
          width: '100%',
          height: '100%',
          display: 'block',
          borderRadius: 'inherit',
          transform: 'none',
          '&::before, &::after': {
            inset: 0,
          },
        }}
      />
    </Box>
  </Button>
);

const FilterToggleButton = ({
  defaultLabel,
  baseIcon,
  filtered,
  tooltipIdle,
  tooltipClear,
  onClick,
  sx,
  hoverBackground,
  hoverColor,
  tooltipPlacement,
}: {
  defaultLabel: string;
  baseIcon: React.ReactNode;
  filtered: boolean;
  tooltipIdle: string;
  tooltipClear: string;
  onClick: () => void;
  sx: Record<string, unknown>;
  hoverBackground: string;
  hoverColor?: string;
  tooltipPlacement: 'top' | 'bottom';
}) => {
  const { t } = useTranslation('settings');
  const [isHovered, setIsHovered] = React.useState(false);
  const showClearState = filtered && isHovered;
  const label = showClearState ? String(t('users.counters.showAllShort')) : defaultLabel;

  return (
    <Tooltip
      title={filtered ? tooltipClear : tooltipIdle}
      enterTouchDelay={0}
      leaveTouchDelay={500}
      arrow
      placement={tooltipPlacement}
    >
      <Button
        type="button"
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          width: '100%',
          minWidth: '8.5rem',
          flexShrink: 0,
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '.2em .8em',
          gap: '.4em',
          borderRadius: '2em',
          border: 'none',
          cursor: 'pointer',
          textTransform: 'none',
          ...sx,
          '&:hover': {
            background: hoverBackground,
            ...(hoverColor ? { color: hoverColor } : {}),
          },
        }}
      >
        {showClearState ? <Clear sx={{ fontSize: 15 }} /> : baseIcon}
        {label}
      </Button>
    </Tooltip>
  );
};

const UserCounters: React.FC<UserCountersProps> = ({
  activeUsers,
  invitedUsers,
  availableLicenses,
  showSkeleton = false,
  onAddUser,
  onToggleActiveFilter,
  onToggleInvitedFilter,
  onToggleInactiveFilter,
  isActiveFilterApplied = false,
  isInvitedFilterApplied = false,
  isInactiveFilterApplied = false,
}) => {
  const { t } = useTranslation('settings');
  const usedLicenses = activeUsers + invitedUsers;
  const safeAvailableLicenses = Math.max(availableLicenses, 0);

  const counterButtonSx = {
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '.2em .8em',
    gap: '.4em',
    borderRadius: '2em',
    border: 'none',
    cursor: 'pointer',
  } as const;

  return (
    <CountersContainer>
      <UsedLicensesContainer>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.8em' }}>
          {showSkeleton ? (
            <SkeletonCounterValue value={`${usedLicenses}`} />
          ) : (
            <LicenseNumber>{`${usedLicenses}`}</LicenseNumber>
          )}
          <div>
            {showSkeleton ? (
              <SkeletonLabel>{String(t(usedLicenses === 1 ? 'users.counters.licenseInUseOne' : 'users.counters.licenseInUseMany'))}</SkeletonLabel>
            ) : (
              <ParagraphContainer style={{ margin: '0em', fontSize: '14px' }}>
                {String(t(usedLicenses === 1 ? 'users.counters.licenseInUseOne' : 'users.counters.licenseInUseMany'))}
              </ParagraphContainer>
            )}
          </div>
        </div>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            backgroundColor: '#c9c9c9',
            mx: { xs: 0, md: 0.5, lg: 2 },
          }}
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '.5em',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {showSkeleton ? (
            <>
              <SkeletonActionButton
                icon={<RadioButtonChecked sx={{ fontSize: 15 }} />}
                text={String(t('users.counters.activeMany'))}
                sx={{
                  ...counterButtonSx,
                  background: '#BBF8BD',
                  color: '#338D31',
                }}
              />
              <SkeletonActionButton
                icon={<Email sx={{ fontSize: 15 }} />}
                text={String(t('users.counters.invitedMany'))}
                sx={{
                  ...counterButtonSx,
                  background: '#F8E7BB',
                  color: '#8D7931',
                }}
              />
            </>
          ) : (
            <>
              <FilterToggleButton
                defaultLabel={String(activeUsers) + ' ' + String(t(activeUsers === 1 ? 'users.counters.activeOne' : 'users.counters.activeMany'))}
                baseIcon={<RadioButtonChecked sx={{ fontSize: 15 }} />}
                filtered={isActiveFilterApplied}
                tooltipIdle={String(t('users.counters.filterActive'))}
                tooltipClear={String(t('users.counters.showAll'))}
                onClick={() => onToggleActiveFilter?.()}
                sx={{
                  background: '#BBF8BD',
                  color: '#338D31',
                }}
                hoverBackground="#9dd1a7"
                tooltipPlacement="top"
              />

              <FilterToggleButton
                defaultLabel={String(invitedUsers) + ' ' + String(t(invitedUsers === 1 ? 'users.counters.invitedOne' : 'users.counters.invitedMany'))}
                baseIcon={<Email sx={{ fontSize: 15 }} />}
                filtered={isInvitedFilterApplied}
                tooltipIdle={String(t('users.counters.filterInvited'))}
                tooltipClear={String(t('users.counters.showAll'))}
                onClick={() => onToggleInvitedFilter?.()}
                sx={{
                  background: '#F8E7BB',
                  color: '#8D7931',
                }}
                hoverBackground="#E8D7AB"
                tooltipPlacement="bottom"
              />
            </>
          )}
        </div>
      </UsedLicensesContainer>

      <AvailableLicenses>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.8em' }}>
          {showSkeleton ? (
            <SkeletonCounterValue value={`${safeAvailableLicenses}`} />
          ) : (
            <LicenseNumber>{`${safeAvailableLicenses}`}</LicenseNumber>
          )}
          <div>
            {showSkeleton ? (
              <SkeletonLabel>{String(t(safeAvailableLicenses === 1 ? 'users.counters.licenseAvailableOne' : 'users.counters.licenseAvailableMany'))}</SkeletonLabel>
            ) : (
              <ParagraphContainer>{String(t(safeAvailableLicenses === 1 ? 'users.counters.licenseAvailableOne' : 'users.counters.licenseAvailableMany'))}</ParagraphContainer>
            )}
          </div>
        </div>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            backgroundColor: '#c9c9c9',
            mx: { xs: 0, md: 2 },
          }}
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '.5em',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {showSkeleton ? (
            <>
              <SkeletonActionButton
                text={String(t('users.counters.addUser'))}
                sx={{
                  ...counterButtonSx,
                  background: '#DBE5F2',
                  color: '#4A5E76',
                }}
              />
              <SkeletonActionButton
                text={String(t('users.counters.reactivateUser'))}
                sx={{
                  ...counterButtonSx,
                  background: '#DBE5F2',
                  color: '#4A5E76',
                }}
              />
            </>
          ) : (
            <>
              <Tooltip title={String(t('users.counters.registerTooltip'))} enterTouchDelay={0} leaveTouchDelay={500} arrow placement="top">
                <Button
                  type="button"
                  onClick={() => onAddUser?.()}
                  sx={{
                    width: '100%',
                    fontSize: 14,
                    background: '#DBE5F2',
                    color: '#4A5E76',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '.2em .8em',
                    gap: '.4em',
                    borderRadius: '2em',
                    border: 'none',
                    cursor: 'pointer',
                    textTransform: 'none',
                    '&:hover': { background: '#546b86', color: '#FFF' },
                  }}
                >
                  {t('users.counters.addUser')}
                </Button>
              </Tooltip>

              <FilterToggleButton
                defaultLabel={String(t('users.counters.reactivateUser'))}
                baseIcon={null}
                filtered={isInactiveFilterApplied}
                tooltipIdle={String(t('users.counters.filterInactive'))}
                tooltipClear={String(t('users.counters.showAll'))}
                onClick={() => onToggleInactiveFilter?.()}
                sx={{
                  background: '#DBE5F2',
                  color: '#4A5E76',
                }}
                hoverBackground="#546b86"
                hoverColor="#FFF"
                tooltipPlacement="bottom"
              />
            </>
          )}
        </div>
      </AvailableLicenses>
    </CountersContainer>
  );
};

export default UserCounters;
